import axios from "axios";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL + '/service-provider',
  timeout: 10000,
  withCredentials: true,
});

// Check if user already has a service application (any status)
export const checkExistingApplication = async () => {
  try {
    const response = await api.get("/my-service");
    if (response.data.success) {
      const services = response.data.services || [];
      // Return the latest application if one exists
      const pending = services.find(s => s.status === 'pending');
      const approved = services.find(s => s.status === 'approved');
      const declined = services.find(s => s.status === 'declined');
      // Determine effective status: approved > declined > pending
      const effectiveStatus = approved ? 'approved' : declined ? 'declined' : pending ? 'pending' : null;
      return {
        success: true,
        hasExisting: services.length > 0,
        latest: services[0] || null,
        effectiveStatus,
        pending: pending || null,
        approved: approved || null,
        declined: declined || null,
      };
    }
    return { success: false, hasExisting: false };
  } catch (error) {
    // 404 or no data = no existing application
    return { success: true, hasExisting: false };
  }
};

// Create service (for service provider application)
export const createService = async (serviceData) => {
  try {
    const response = await api.post("/", serviceData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error creating service:", error);
    // Surface the specific error message from the server
    const serverMessage = error.response?.data?.message || error.response?.data?.error;
    const serverErrors = error.response?.data?.errors;
    let detail = "Failed to create service.";
    if (serverMessage) {
      detail = serverMessage;
    }
    if (serverErrors && typeof serverErrors === 'object') {
      detail += ' ' + Object.values(serverErrors).join(', ');
    }
    return {
      success: false,
      error: detail
    };
  }
};

// Get services (for dropdown selection) — used by ServiceProviderApplication
export const userGetServices = async () => {
  try {
    const response = await api.get("/available-services");
    if (response.data.success) {
      return { success: true, services: response.data.services || [] };
    } else {
      return { success: false, error: response.data.message || "Failed to load services" };
    }
  } catch (error) {
    console.error("Error in userGetServices:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Failed to load services. Please check your connection."
    };
  }
};

// Get service providers by service ID — public, used on service browsing pages
export const getServiceProviders = async (serviceId) => {
  try {
    const response = await api.get(`/service-provider/${serviceId}/providers`);
    return { success: true, providers: response.data.providers || [] };
  } catch (error) {
    return { success: false, error: error.response?.data?.message || "Failed to fetch service providers" };
  }
};

// Get my service provider profile — used by Dashboard
export const getMyProviderProfile = async () => {
  try {
    const response = await api.get("/service-provider/profile");
    return { success: true, profile: response.data.profile || response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.message || "Failed to fetch profile" };
  }
};

// Get provider's bookings — used by Dashboard
export const getProviderBookings = async () => {
  try {
    const response = await api.get("/service-provider/bookings");
    return { success: true, bookings: response.data.bookings || [] };
  } catch (error) {
    return { success: false, error: error.response?.data?.message || "Failed to fetch bookings" };
  }
};

// Update booking status (for providers) — used by Dashboard
export const updateProviderBookingStatus = async (bookingId, statusData) => {
  try {
    const response = await api.put(`/service-provider/booking/${bookingId}/status`, statusData);
    return { success: true, message: response.data.message, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.message || "Failed to update booking status" };
  }
};

// Get all public services for homepage
// Accepts filters: { location, category, minPrice, maxPrice, availability }
export const getPublicServices = async (filters = {}) => {
  try {
    const response = await api.get("/services", { params: filters });
    if (response.data.success) {
      return { success: true, services: response.data.data || [], count: response.data.count || 0 };
    } else {
      return { success: false, error: response.data.message || "Failed to load service offers" };
    }
  } catch (error) {
    console.error("Error in getPublicServices:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Failed to load service offers. Please check your connection."
    };
  }
};

// Get single offer details (public)
export const getPublicOfferDetails = async (offerId) => {
  try {
    const publicApi = axios.create({
      baseURL: import.meta.env.VITE_API_URL || "http://localhost:5000/api",
      timeout: 10000,
    });
    const response = await publicApi.get(`/service-provider/offers/${offerId}`);
    if (response.data.success) {
      return { success: true, data: response.data.data, offer: response.data.data };
    } else {
      return { success: false, error: response.data.message || "Failed to load offer details" };
    }
  } catch (error) {
    console.error("Error in getPublicOfferDetails:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Failed to load offer details. Please check your connection."
    };
  }
};

// Public Service Offers
export const getPublicServiceOffers = async (serviceId) => {
  try {
    const response = await api.get(`/services/${serviceId}/offers`);
    if (response.data.success) {
      return { success: true, data: response.data.data };
    } else {
      return { success: false, error: response.data.message || "Failed to load service offers" };
    }
  } catch (error) {
    return { success: false, error: error.response?.data?.message || "Failed to load service offers" };
  }
};